import React, { useState } from 'react';
import { Eye, EyeOff, Lock, Key, X } from 'lucide-react';
import Button from '../../common/Button/Button';
import { ErrorMessage } from '../../../utils/formValidation';

const ResetPassword = ({ onBackToLogin, onResetPassword, loading = false }) => {
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [errors, setErrors] = useState({});

  const validateFieldLevel = (name, value) => {
    let fieldError = '';

    if (name === 'currentPassword') {
      if (!value) fieldError = 'Current password is required';
    } else if (name === 'newPassword') {
      if (!value) {
        fieldError = 'New password is required';
      } else if (value.length < 6) {
        fieldError = 'New password must be at least 6 characters';
      } else if (formData.currentPassword && value === formData.currentPassword) {
        fieldError = 'New password must be different from current password';
      }
    } else if (name === 'confirmPassword') {
      if (!value) {
        fieldError = 'Please confirm your new password';
      } else if (value !== formData.newPassword) {
        fieldError = 'Passwords do not match';
      }
    }

    setErrors(prev => ({
      ...prev,
      [name]: fieldError
    }));

    return !fieldError;
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    validateFieldLevel(name, value);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    setErrors(prev => ({
      ...prev,
      [name]: ''
    }));
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const validateAllFields = () => {
    const currentPassValid = validateFieldLevel('currentPassword', formData.currentPassword);
    const newPassValid = validateFieldLevel('newPassword', formData.newPassword);
    const confirmPassValid = validateFieldLevel('confirmPassword', formData.confirmPassword);

    return currentPassValid && newPassValid && confirmPassValid;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (validateAllFields()) {
      onResetPassword(formData.currentPassword, formData.newPassword);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-agrosaf-green to-agrosaf-blue relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute top-10 right-10 w-32 h-32 bg-white bg-opacity-10 rounded-full"></div>
        <div className="absolute bottom-20 left-20 w-24 h-24 bg-white bg-opacity-10 rounded-full"></div>

        <div className="flex flex-col justify-center px-12 text-white relative z-10">
          {/* Logo */}
          <div className="mb-8">
            <div className="flex items-center mb-6">
              <img src="/logo.png" alt={`${process.env.REACT_APP_COMPANY_NAME} Logo`} className="h-16 w-auto object-contain mr-4" />
              <div className="flex flex-col text-white">
                <span className="text-xl font-bold tracking-tight">{process.env.REACT_APP_COMPANY_NAME}</span>
              </div>
            </div>
            <p className="text-sm opacity-80">{process.env.REACT_APP_COMPANY_TAGLINE}</p>
          </div>

          {/* Main Title */}
          <div className="mb-8">
            <h1 className="text-6xl font-bold mb-4">RESET</h1>
            <p className="text-xl opacity-90">Change Your Password</p>
          </div>

          {/* Info Box */}
          <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-lg p-6 border border-white border-opacity-20">
            <p className="text-sm mb-2">Enter your current password and choose a new secure password.</p>
            <p className="text-sm">Make sure your new password is strong and unique.</p>
          </div>
        </div>
      </div>

      {/* Right Panel - Reset Password Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-8 bg-white">
        <div className="w-full max-w-md">
          {/* Header with Back Button */}
          <div className="flex items-center justify-between mb-8">
            <button
              onClick={onBackToLogin}
              className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
            >
              <X className="h-5 w-5 mr-2" />
              Back
            </button>
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 flex items-center justify-center overflow-hidden">
                <img src="/logo.png" alt={`${process.env.REACT_APP_COMPANY_NAME} Logo`} className="w-full h-full object-contain" />
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Reset Password</h2>
            <p className="text-gray-600">Enter your current password and choose a new one</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" autoComplete="off">
            {/* Current Password Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Current Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type={showPasswords.current ? 'text' : 'password'}
                  name="currentPassword"
                  value={formData.currentPassword}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  className={`input-field pl-10 pr-10 ${errors.currentPassword ? 'border-red-500 focus:ring-red-500' : ''}`}
                  placeholder="Enter your current password"
                  autoComplete="current-password"
                  data-form-type="other"
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility('current')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPasswords.current ? <Eye className="h-5 w-5" /> : <EyeOff className="h-5 w-5" />}
                </button>
              </div>
              <ErrorMessage error={errors.currentPassword} />
            </div>

            {/* New Password Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                New Password
              </label>
              <div className="relative">
                <Key className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type={showPasswords.new ? 'text' : 'password'}
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  className={`input-field pl-10 pr-10 ${errors.newPassword ? 'border-red-500 focus:ring-red-500' : ''}`}
                  placeholder="Enter your new password"
                  autoComplete="new-password"
                  data-form-type="other"
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility('new')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPasswords.new ? <Eye className="h-5 w-5" /> : <EyeOff className="h-5 w-5" />}
                </button>
              </div>
              <ErrorMessage error={errors.newPassword} />
            </div>

            {/* Confirm New Password Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Confirm New Password
              </label>
              <div className="relative">
                <Key className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type={showPasswords.confirm ? 'text' : 'password'}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  className={`input-field pl-10 pr-10 ${errors.confirmPassword ? 'border-red-500 focus:ring-red-500' : ''}`}
                  placeholder="Confirm your new password"
                  autoComplete="new-password"
                  data-form-type="other"
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility('confirm')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPasswords.confirm ? <Eye className="h-5 w-5" /> : <EyeOff className="h-5 w-5" />}
                </button>
              </div>
              <ErrorMessage error={errors.confirmPassword} />
            </div>

            {/* Password Requirements */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-blue-900 mb-2">Password Requirements:</h4>
              <ul className="text-xs text-blue-800 space-y-1">
                <li>• At least 6 characters long</li>
                <li>• Different from your current password</li>
                <li>• Use a combination of letters, numbers, and symbols for better security</li>
              </ul>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              loading={loading}
              className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
            >
              Reset
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
